import React from 'react';

import { useContextRequired } from '@nfctron/nexus-react';

import { Loader, Text } from '@mantine/core';
import Image from 'next/image';

import { getEventTypeSensitiveComponentDisplayName } from '@root/pages/event/[organizer]/[event]/_resources/helpers/common';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../../components/CartContext';
import { TicketCardExtraProducts } from '../../components/Ticket/TicketCardExtraProducts';
import { cartStoreSelectors, useCartStore, useExtraProductsApi } from '../../stores/cartStore';
import { ConfigSheetAtom } from '../ConfigSheet/ConfigSheet.atom';

/**
 * Event/Cart/ProductsViewContent atom component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ProductsViewContentAtom: AtomComponent = () => {
	const { event } = useContextRequired(EventCartPageContext);
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ProductsViewContentAtom.displayName);
	const { t: typeSensitiveT } = useTranslate(
		APP_I18N_NAMESPACE.MAIN,
		getEventTypeSensitiveComponentDisplayName(ProductsViewContentAtom.displayName, event.eventType.eventTypeId),
	);
	const cartedTicketsWithExtraProducts = useCartStore((state) => cartStoreSelectors.listCartedTicketsWithExtraProducts(state));

	const { extraProductsApi } = useExtraProductsApi();

	return (
		<React.Fragment>
			{/* ticket listing section */}
			<section className="flex flex-col gap-2">
				{/* heading */}
				<Text fw={700} size="md" c="dimmed">
					{typeSensitiveT('heading')}
				</Text>
				{/* ticket types */}
				{extraProductsApi.isLoading ? (
					<div className="flex grow flex-col items-center justify-center gap-8 rounded-xl bg-white p-4">
						{/* hero image */}
						<Image className="mx-auto" src="/static/images/blob.svg" alt={t('loading.title')} width={300} height={300} />
						{/* loader */}
						<div className="mx-auto">
							<Loader variant="dots" size="xl" c="lucky-point" />
						</div>
						<div>
							<Text c="lucky-point" fw={700} ta="center">
								{t('loading.title')}
							</Text>
							<Text c="dimmed" ta="center" size="md">
								{t('loading.text', { eventName: event.namePub })}
							</Text>
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-3">
						{cartedTicketsWithExtraProducts.map((ticket) => (
							<TicketCardExtraProducts key={ticket.cartTicketId} cartTicketId={ticket.cartTicketId} />
						))}
					</div>
				)}
			</section>

			{/* config sheet */}
			<ConfigSheetAtom />
		</React.Fragment>
	);
};

/**
 * Event/Cart/ProductsViewContent atom display name
 * @type {ComponentDisplayName<"atom">>}
 */
ProductsViewContentAtom.displayName = 'Event/Cart/ProductsViewContentAtom';
